import { useEffect, useId, useState } from 'react';
import type { PublicSourceView } from '@urbanrats/contracts';
import type { ClientGateways } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';

interface Props {
  gw: ClientGateways;
  /** Sources matched to the current game (shown first). */
  suitable?: readonly string[];
  activeId: string;
  onSimulation: () => void;
  onSource: (v: PublicSourceView) => void;
  onUserFile: (file: File, attribution: string) => void;
}

export function SourcePanel({ gw, activeId, onSimulation, onSource, onUserFile, suitable = [] }: Props) {
  const { t } = useI18n();
  const [views, setViews] = useState<PublicSourceView[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rights, setRights] = useState(false);
  const [attribution, setAttribution] = useState('');
  const ids = { file: useId(), rights: useId(), attr: useId() };

  useEffect(() => {
    let live = true;
    gw.media
      .listSources()
      .then((v) => live && setViews(v))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [gw]);

  const usableAll = views?.filter((v) => v.selectable) ?? [];
  // Webcam is always offered; files are matched to the game's capability.
  const usable = usableAll.filter((v) => suitable.includes(v.sourceId) || v.mediaType === 'VIDEO_WEBCAM');
  const others = usableAll.filter((v) => !usable.includes(v));
  const blocked = views?.filter((v) => !v.selectable) ?? [];
  const reason = (r: string) => (t(`srcreason.${r}` as MessageKey) === `srcreason.${r}` ? r : t(`srcreason.${r}` as MessageKey));

  return (
    <div className="sources" data-testid="source-panel">
      <ul className="source-list">
        <li className={`source-item ${activeId === 'simulation' ? 'active' : ''}`}>
          <div className="si-head">
            <strong>{t('source.simulation')}</strong>
            <span className="chip mode mode-SIMULATION">{t('mode.SIMULATION')}</span>
          </div>
          <p className="note">{t('source.simulationHint')}</p>
          <button type="button" className="ghost-btn bordered" disabled={activeId === 'simulation'} onClick={onSimulation}>
            {activeId === 'simulation' ? t('source.inUse') : t('source.use')}
          </button>
        </li>
      </ul>

      {failed && <p className="field-error">{t('source.loadError')}</p>}
      {!views && !failed && <p className="muted">…</p>}

      {usable.length > 0 && (
        <>
          <h3>{t('source.suitable')}</h3>
          <ul className="source-list">
            {usable.map((v) => (
              <li key={v.sourceId} className={`source-item ${activeId === v.sourceId ? 'active' : ''}`} data-testid={`src-${v.sourceId}`}>
                <div className="si-head">
                  <strong>{v.displayName}</strong>
                  <span className={`chip mode mode-${v.sourceMode}`}>{t(`mode.${v.sourceMode}` as MessageKey)}</span>
                </div>
                <p className="note">{t('source.licence', { licence: v.licence })}</p>
                {v.attribution && <p className="note">{v.attribution}</p>}
                {v.mediaType === 'VIDEO_WEBCAM' && <p className="note">{t('source.webcamNote')}</p>}
                <button type="button" className="ghost-btn bordered" disabled={activeId === v.sourceId} onClick={() => onSource(v)}>
                  {activeId === v.sourceId ? t('source.inUse') : t('source.use')}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {others.length > 0 && (
        <>
          <h3>{t('source.otherGames')}</h3>
          <ul className="source-list">
            {others.map((v) => (
              <li key={v.sourceId} className={`source-item ${activeId === v.sourceId ? 'active' : ''}`} data-testid={`src-${v.sourceId}`}>
                <div className="si-head">
                  <strong>{v.displayName}</strong>
                  <span className={`chip mode mode-${v.sourceMode}`}>{t(`mode.${v.sourceMode}` as MessageKey)}</span>
                </div>
                <button type="button" className="ghost-btn bordered" disabled={activeId === v.sourceId} onClick={() => onSource(v)}>
                  {activeId === v.sourceId ? t('source.inUse') : t('source.use')}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <form
        className="add-video"
        onSubmit={(e) => {
          e.preventDefault();
          if (file && rights) onUserFile(file, attribution.trim());
        }}
      >
        <h3>{t('source.addTitle')}</h3>
        <label htmlFor={ids.file}>{t('source.addFile')}</label>
        <input id={ids.file} type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="add-file" />
        <label htmlFor={ids.attr}>{t('source.addAttribution')}</label>
        <input id={ids.attr} type="text" maxLength={200} value={attribution} onChange={(e) => setAttribution(e.target.value)} />
        <label className="check-row" htmlFor={ids.rights}>
          <input id={ids.rights} type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} /> {t('source.addRights')}
        </label>
        <p className="note">{t('source.addRightsNote')}</p>
        <button type="submit" className="ghost-btn bordered" disabled={!file || !rights}>
          {t('source.addButton')}
        </button>
      </form>

      {blocked.length > 0 && (
        <>
          <h3>{t('source.unavailable')}</h3>
          <ul className="source-list">
            {blocked.map((v) => (
              <li key={v.sourceId} className="source-item blocked-src" data-testid={`src-${v.sourceId}`}>
                <div className="si-head">
                  <strong>{v.displayName}</strong>
                  <span className="chip">{v.readiness}</span>
                </div>
                <p className="note">{t('source.owner', { owner: v.owner })}</p>
                <ul className="reasons">
                  {v.blockedReasons.map((r) => (
                    <li key={r}>✕ {reason(r)}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
